import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Copy, Eye, EyeOff, RefreshCw, Loader2, Save, Key, Hash,
  ChevronLeft, ChevronRight, FileText, Clock, Users, ScanLine, Trash2, Pencil, Plus
} from "lucide-react";
import { API } from "@/lib/api";

const DEFAULT_PERMS = { can_add_points: true, can_issue_reward: true, can_redeem: true };

const TIMEZONES = [
  "UTC",
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Rome", "Europe/Amsterdam", "Europe/Lisbon", "Europe/Athens", "Europe/Moscow",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Sao_Paulo", "America/Mexico_City",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo",
  "Asia/Shanghai", "Australia/Sydney", "Pacific/Auckland",
];

export default function VendorSettingsPage() {
  const { authHeaders, user } = useAuth();
  const vid = user?.vendor_id;
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedClientId, setCopiedClientId] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [editClientId, setEditClientId] = useState("");
  const [clientIdSaving, setClientIdSaving] = useState(false);
  const [clientIdError, setClientIdError] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [tzSaving, setTzSaving] = useState(false);
  const [tzSaved, setTzSaved] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [apiLogsTotal, setApiLogsTotal] = useState(0);
  const [apiLogsPage, setApiLogsPage] = useState(1);
  const [apiLogsLoading, setApiLogsLoading] = useState(false);

  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffForm, setStaffForm] = useState({
    email: "", name: "", password: "", permissions: { ...DEFAULT_PERMS },
  });
  const [staffSaving, setStaffSaving] = useState(false);
  const [staffError, setStaffError] = useState("");
  const [editingStaff, setEditingStaff] = useState(null);
  const [revealedKey, setRevealedKey] = useState("");

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff`, { headers: authHeaders() });
      setStaffList(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    finally { setStaffLoading(false); }
  }, [authHeaders]);

  const load = async () => {
    if (!vid) return;
    try {
      const { data } = await axios.get(`${API}/vendors/${vid}`, { headers: authHeaders() });
      setVendor(data);
      setEditClientId(data.custom_client_id || "");
      setTimezone(data.timezone || "UTC");
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadApiLogs = useCallback(async (page = 1) => {
    if (!vid) return;
    setApiLogsLoading(true);
    try {
      const { data } = await axios.get(`${API}/vendors/${vid}/api-logs?page=${page}&limit=20`, { headers: authHeaders() });
      setApiLogs(data.logs || []);
      setApiLogsTotal(data.total || 0);
      setApiLogsPage(page);
    } catch (err) { console.error(err); }
    finally { setApiLogsLoading(false); }
  }, [vid, authHeaders]);

  useEffect(() => { load(); }, [vid]);
  useEffect(() => { if (vid) loadApiLogs(1); }, [vid]);
  useEffect(() => { if (vid) loadStaff(); }, [vid, loadStaff]);

  const resetStaffForm = () => {
    setStaffForm({ email: "", name: "", password: "", permissions: { ...DEFAULT_PERMS } });
    setEditingStaff(null);
    setStaffError("");
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setStaffSaving(true);
    setStaffError("");
    try {
      await axios.post(`${API}/staff`, {
        email: staffForm.email.trim().toLowerCase(),
        name: staffForm.name.trim(),
        password: staffForm.password,
        permissions: staffForm.permissions,
      }, { headers: authHeaders() });
      resetStaffForm();
      await loadStaff();
    } catch (err) {
      setStaffError(err.response?.data?.detail || "Failed to create staff");
    } finally { setStaffSaving(false); }
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    if (!editingStaff) return;
    setStaffSaving(true);
    setStaffError("");
    try {
      const body = {
        name: staffForm.name.trim(),
        permissions: staffForm.permissions,
      };
      if (staffForm.password) body.password = staffForm.password;
      await axios.patch(`${API}/staff/${editingStaff.id}`, body, { headers: authHeaders() });
      resetStaffForm();
      await loadStaff();
    } catch (err) {
      setStaffError(err.response?.data?.detail || "Failed to update staff");
    } finally { setStaffSaving(false); }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm("Delete this staff account?")) return;
    try {
      await axios.delete(`${API}/staff/${id}`, { headers: authHeaders() });
      if (editingStaff?.id === id) resetStaffForm();
      await loadStaff();
    } catch (err) { console.error(err); }
  };

  const startEditStaff = (s) => {
    setEditingStaff(s);
    setStaffForm({
      email: s.email,
      name: s.name || "",
      password: "",
      permissions: { ...DEFAULT_PERMS, ...(s.permissions || {}) },
    });
    setStaffError("");
  };

  const togglePerm = (key) => {
    setStaffForm((f) => ({
      ...f,
      permissions: { ...f.permissions, [key]: !f.permissions[key] },
    }));
  };

  const displayKey = revealedKey || vendor?.api_key || "";
  const keyHint = vendor?.api_key_prefix
    ? `${vendor.api_key_prefix}…${vendor.api_key_last4 || ""}`
    : (vendor?.api_key ? `${vendor.api_key.slice(0, 12)}…` : "No key");

  const handleCopy = () => {
    if (!displayKey) return;
    navigator.clipboard.writeText(displayKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCopyClientId = () => { navigator.clipboard.writeText(editClientId || vid); setCopiedClientId(true); setTimeout(() => setCopiedClientId(false), 2000); };

  const handleRegen = async () => {
    if (!window.confirm("Regenerate API key? Old key will stop working. The new key is shown only once.")) return;
    setRegenLoading(true);
    try {
      const { data } = await axios.post(`${API}/vendors/${vid}/regenerate-key`, {}, { headers: authHeaders() });
      setRevealedKey(data.api_key || "");
      await load();
    } catch (err) { console.error(err); }
    finally { setRegenLoading(false); }
  };

  const handleSaveClientId = async () => {
    setClientIdSaving(true);
    setClientIdError("");
    try {
      await axios.put(`${API}/vendors/${vid}`, { custom_client_id: editClientId.trim() }, { headers: authHeaders() });
      await load();
    } catch (err) {
      setClientIdError(err.response?.data?.detail || "Failed to save");
    } finally { setClientIdSaving(false); }
  };

  const handleSaveTimezone = async () => {
    setTzSaving(true);
    setTzSaved(false);
    try {
      await axios.put(`${API}/vendors/${vid}`, { timezone }, { headers: authHeaders() });
      setTzSaved(true);
      setTimeout(() => setTzSaved(false), 2000);
    } catch (err) { console.error(err); }
    finally { setTzSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-ink/40"><Loader2 size={20} className="animate-spin" /></div>;
  if (!vendor) return null;

  const logPages = Math.ceil(apiLogsTotal / 20);

  return (
    <div className="space-y-5 max-w-4xl" data-testid="vendor-settings-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Settings</h1>
          <p className="text-sm text-ink/40 mt-0.5">Manage staff, Client ID, API key and view API logs</p>
        </div>
        <Link
          to="/scan"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-purple-500 text-white text-sm font-medium hover:bg-purple-600"
          data-testid="open-scanner-link"
        >
          <ScanLine size={14} /> Open scanner
        </Link>
      </div>

      <Card className="bg-white border-0 shadow-sm rounded-2xl" data-testid="staff-management-card">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Users size={14} className="text-ink/50" /> Staff accounts
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <p className="text-xs text-ink/40">
            Staff can only use the till scanner. Choose which actions each person may perform.
          </p>

          <form onSubmit={editingStaff ? handleUpdateStaff : handleCreateStaff} className="space-y-3 rounded-xl bg-surface p-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-ink/50">Name</Label>
                <Input
                  value={staffForm.name}
                  onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                  required
                  className="h-9 text-sm"
                  data-testid="staff-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-ink/50">Email</Label>
                <Input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  required={!editingStaff}
                  disabled={!!editingStaff}
                  className="h-9 text-sm"
                  data-testid="staff-email-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-ink/50">{editingStaff ? "New password (optional)" : "Password"}</Label>
                <Input
                  type="password"
                  value={staffForm.password}
                  onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                  required={!editingStaff}
                  minLength={editingStaff ? undefined : 6}
                  className="h-9 text-sm"
                  data-testid="staff-password-input"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-ink">
              {[
                ["can_add_points", "Add points/stamps"],
                ["can_issue_reward", "Issue reward"],
                ["can_redeem", "Redeem"],
              ].map(([key, label]) => (
                <label key={key} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!staffForm.permissions[key]}
                    onChange={() => togglePerm(key)}
                    className="rounded border-line"
                    data-testid={`staff-perm-${key}`}
                  />
                  {label}
                </label>
              ))}
            </div>
            {staffError && <p className="text-xs text-danger">{typeof staffError === "string" ? staffError : "Error"}</p>}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={staffSaving} size="sm" className="h-8 gap-1 rounded-pill bg-purple-500 hover:bg-purple-600 text-white" data-testid="staff-save-btn">
                {staffSaving ? <Loader2 size={12} className="animate-spin" /> : editingStaff ? <Save size={12} /> : <Plus size={12} />}
                {editingStaff ? "Update staff" : "Create staff"}
              </Button>
              {editingStaff && (
                <Button type="button" variant="outline" size="sm" className="h-8 rounded-pill" onClick={resetStaffForm}>
                  Cancel
                </Button>
              )}
            </div>
          </form>

          {staffLoading ? (
            <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-ink/30" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Permissions</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffList.map((s) => (
                  <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                    <TableCell className="text-sm font-medium text-ink">{s.name}</TableCell>
                    <TableCell className="text-xs text-ink/60">{s.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {s.permissions?.can_add_points && <Badge variant="outline" className="text-[10px]">Earn</Badge>}
                        {s.permissions?.can_issue_reward && <Badge variant="outline" className="text-[10px]">Issue</Badge>}
                        {s.permissions?.can_redeem && <Badge variant="outline" className="text-[10px]">Redeem</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <button type="button" onClick={() => startEditStaff(s)} className="p-1.5 text-ink/40 hover:text-ink rounded hover:bg-purple-50" data-testid={`edit-staff-${s.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDeleteStaff(s.id)} className="p-1.5 text-ink/40 hover:text-danger rounded hover:bg-danger/10" data-testid={`delete-staff-${s.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {staffList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-ink/40 py-6 text-sm">No staff accounts yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Client ID */}
        <Card className="bg-white border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Hash size={14} className="text-ink/50" /> Client ID</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4 space-y-2">
            <p className="text-xs text-ink/40">Used as <code className="bg-purple-50 px-1 rounded text-[10px]">client_id</code> in API calls.</p>
            <div className="flex items-center gap-1.5">
              <Input value={editClientId} onChange={(e) => { setEditClientId(e.target.value); setClientIdError(""); }} placeholder="e.g. my-shop" className="flex-1 h-9 text-xs font-mono" data-testid="client-id-input" />
              <button onClick={handleCopyClientId} className="p-2 text-ink/30 hover:text-ink rounded hover:bg-purple-50 shrink-0"><Copy size={14} /></button>
            </div>
            {clientIdError && <p className="text-xs text-danger">{clientIdError}</p>}
            {copiedClientId && <p className="text-xs text-purple-500">Copied!</p>}
            <Button variant="outline" size="sm" onClick={handleSaveClientId} disabled={clientIdSaving} className="h-7 text-xs gap-1 rounded-full">
              {clientIdSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </Button>
          </CardContent>
        </Card>

        {/* API Key */}
        <Card className="bg-white border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Key size={14} className="text-ink/50" /> API Key</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4 space-y-2">
            <div className="flex items-center gap-1.5">
              <code className="flex-1 min-w-0 px-3 py-2 bg-surface border border-ink/10 rounded-lg text-xs font-mono text-ink/70 truncate">
                {displayKey ? (showKey ? displayKey : "cl_" + "\u2022".repeat(28)) : keyHint}
              </code>
              {displayKey ? (
                <button onClick={() => setShowKey(v => !v)} className="p-2 text-ink/30 hover:text-ink rounded hover:bg-purple-50"><Eye size={14} /></button>
              ) : null}
              <button onClick={handleCopy} disabled={!displayKey} className="p-2 text-ink/30 hover:text-ink rounded hover:bg-purple-50 disabled:opacity-40"><Copy size={14} /></button>
            </div>
            {revealedKey && <p className="text-xs text-warning">Copy this key now — it will not be shown again.</p>}
            {copied && <p className="text-xs text-purple-500">Copied!</p>}
            <Button variant="outline" size="sm" onClick={handleRegen} disabled={regenLoading} className="h-7 text-xs gap-1 rounded-full">
              {regenLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerate
            </Button>
            <p className="text-[10px] text-ink/30">Header: <code className="bg-purple-50 px-1 rounded">X-API-Key: {keyHint}</code></p>
          </CardContent>
        </Card>
      </div>

      {/* Timezone */}
      <Card className="bg-white border-0 shadow-sm rounded-2xl" data-testid="timezone-card">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Clock size={14} className="text-ink/50" /> Business Timezone</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4 space-y-2">
          <p className="text-xs text-ink/40">Earning-rule schedules (days, hours &amp; date ranges) are evaluated in this timezone. Set it to your stores&apos; local time so &ldquo;happy hour&rdquo; and double-point days fire correctly.</p>
          <div className="flex items-center gap-2 max-w-md">
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="h-9 text-sm" data-testid="timezone-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleSaveTimezone} disabled={tzSaving} className="h-9 gap-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-full shrink-0" data-testid="save-timezone-btn">
              {tzSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </Button>
          </div>
          {tzSaved && <p className="text-xs text-success">Timezone saved</p>}
        </CardContent>
      </Card>

      {/* API Logs */}
      <Card className="bg-white border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2 pt-5 px-5">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText size={14} className="text-ink/50" /> API Logs ({apiLogsTotal})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {apiLogsLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={16} className="animate-spin text-ink/30" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-widest">Endpoint</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Method</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Time</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {apiLogs.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-mono text-ink/70">{l.endpoint || l.path || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{l.method || "POST"}</Badge></TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${l.status_code < 400 ? "bg-purple-100 text-success" : "bg-danger/10 text-danger"}`}>
                        {l.status_code || "200"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-ink/40">{l.created_at?.slice(0, 19).replace("T", " ") || "—"}</TableCell>
                  </TableRow>
                ))}
                {apiLogs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-ink/40 py-8">No API logs yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          {logPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-ink/5">
              <p className="text-xs text-ink/40">{apiLogsTotal} total</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={apiLogsPage <= 1} onClick={() => loadApiLogs(apiLogsPage - 1)} className="h-7 w-7 p-0"><ChevronLeft size={14} /></Button>
                <span className="text-xs text-ink/60">{apiLogsPage} / {logPages}</span>
                <Button variant="outline" size="sm" disabled={apiLogsPage >= logPages} onClick={() => loadApiLogs(apiLogsPage + 1)} className="h-7 w-7 p-0"><ChevronRight size={14} /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
